import type { RouteLocationNormalizedGeneric } from 'vue-router';
import type { SectionId } from '~/types/sectionId';
import { findSectionConfigById } from '~/utils/findSectionConfigById';
import { resolveSectionPath } from '~/utils/resolveSectionPath';

export function useSectionConfigById(sectionId: SectionId, route?: RouteLocationNormalizedGeneric) {
    const { $subSectionRegistry } = useNuxtApp();
    const { country } = useLanguage(route);

    return computed(() => findSectionConfigById($subSectionRegistry.value, country.value, sectionId));
}

export function useSectionPathById(sectionId: SectionId, route?: RouteLocationNormalizedGeneric) {
    const sectionConfig = useSectionConfigById(sectionId, route);
    const { lang } = useLanguage(route);
    const countryLanguages = useCountryLanguages();

    return computed(() => {
        if (!sectionConfig.value) return null;
        return resolveSectionPath(sectionConfig.value, lang.value, countryLanguages);
    });
}

/**
 * Returns an empty path when the section is mounted at the country root and throws when it cannot be resolved.
 */
export function useRequiredSectionPathById(sectionId: SectionId, route?: RouteLocationNormalizedGeneric) {
    const sectionPath = useSectionPathById(sectionId, route);

    return computed(() => {
        if (sectionPath.value === null) {
            throw new Error(`Section path could not be resolved: ${sectionId}`);
        }
        return sectionPath.value;
    });
}
